import React, { useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from '../../../../store/hooks'
import { KonvaEventObject } from "konva/lib/Node";
import { addItemToSelection, exitSelection, replaceShape } from "../../../../store/model";
import { getPointerModelPosition, } from "../../util/stageManipulation";
import Hotspot from "../../Hotspot";
import TextBase, { createTextBase } from "./TextBase";
import { createTextRubber } from "../../tools/TextRubber";
import { Transform } from "../../../../geometry/transformation";
import { lineRectIntersection } from "../../../../geometry/operation";
import { JSONGeometricShape } from "../../../Palettes/Headerbar/toolbarButtonOpen";
import { getAnchor, rotateAnchoredShapeToTransform } from "../shapeHelper/anchored";
import { RootState } from "../../../../store/store";
//import BaseShape from "../ShapeBase";
import Konva from "konva";

interface TextProps {
    shapeProps: TextShape;
    onSelect: (e: KonvaEventObject<MouseEvent>) => void;
}

function initHostpot(points: Point2d[]): Hotspot[] {
    if (!points) return [];
    return points.map((v, i) => {
        return {
            ...v,
            id: i.toString(),
            isDragging: false,
            possibleInteractions: ["move-object", "move-point"]
        }
    })
}
const Text = ({
    shapeProps,
    onSelect,
}: TextProps) => {


    const dispatch = useAppDispatch();
    const selection = useAppSelector((state: RootState) => state.model.present.selected);
    const currentTool = useAppSelector((state: RootState) => state.tool.currentTool)
    const toolSettings = useAppSelector((state: RootState) => state.tool.toolSettings)
    let textRef = useRef<Konva.Text | null>(null)
    const [hotspots] = useState<Hotspot[]>(initHostpot([{ x: shapeProps.x, y: shapeProps.y }]));
    const [dragStartPoint, setDragStartPoint] = useState<Point2d>({ x: 0, y: 0 });
    const [localTransform, setLocalTransform] = useState<Mat5>([
        [1, 0, 0, 0],
        [0, 1, 0, 0],
        [0, 0, 1, 0],
        [0, 0, 0, 1]]);
    const selected = selection.selectedItemIds.includes(shapeProps.id);
    const shapeTransformer = new Transform(shapeProps.transformation);

    const handleHotpsotDragMove = (e: KonvaEventObject<MouseEvent>) => {
        let pos = getPointerModelPosition(e)
        const move: Point2d = {
            x: pos.x - dragStartPoint.x,
            y: pos.y - dragStartPoint.y
        }
        setLocalTransform(Transform.translationTransform(move.x, move.y).m)
    }

    const handleHotspotDragStart = (e: KonvaEventObject<MouseEvent>) => {
        setDragStartPoint(getPointerModelPosition(e))
    }

    const handleHotspotDragEnd = (e: KonvaEventObject<MouseEvent>) => {
        moveObject();
        setLocalTransform([
            [1, 0, 0, 0],
            [0, 1, 0, 0],
            [0, 0, 1, 0],
            [0, 0, 0, 1]])
    }
    const moveObject = () => {
        dispatch(replaceShape({ ...shapeProps, transformation: shapeTransformer.m }))
    }

    const onTextDragStart = (e: KonvaEventObject<MouseEvent>) => {
        const id = e.target.id();
        dispatch(exitSelection());
        dispatch(addItemToSelection(parseInt(id)))
        setDragStartPoint(getPointerModelPosition(e))
    }

    const handleTextDragMove = handleHotpsotDragMove;
    const onTextDragEnd = handleHotspotDragEnd;

    // Mouse Interactions
    const moveText: MouseInteraction = {
        handleDragStart: onTextDragStart,
        handleDragMove: handleTextDragMove,
        handleDragEnd: onTextDragEnd
    }

    const mouseInteractionMap = new Map<EditType, MouseInteraction>();
    mouseInteractionMap.set("move", moveText);

    shapeTransformer.multiply(new Transform(localTransform))
    const editingMode = selection.selectedItemIds.length === 1 ?
        toolSettings.line.currentEditMode :
        toolSettings[currentTool].currentEditMode;

    const currentMouseInteraction = mouseInteractionMap.get(editingMode);

    return (

        // <BaseShape ref={textRef} baseShapeProps={shapeProps}>
        <>
            <TextBase
                setRef={text => textRef.current = text}
                shapeProps={{ ...shapeProps, transformation: shapeTransformer.m }}
                onClick={onSelect}
                isSelected={selected}
                interaction={currentMouseInteraction}
            />
            {selected && hotspots.map<React.JSX.Element>((hotspot, index) =>
                <Hotspot
                    key={`${index}-hotspot-${index}`}
                    shapeProps={{ ...hotspot, ...(shapeTransformer.point2d(hotspot)) }}
                    interaction={{
                        handleDragStart: handleHotspotDragStart,
                        handleDragMove: handleHotpsotDragMove,
                        handleDragEnd: handleHotspotDragEnd,
                    }}
                />
            )
            }
        </>
        // </BaseShape>
    );
}

const createText = (
    shapeProps: GeometricShape,
    onSelect: (e: KonvaEventObject<MouseEvent>) => void,
) => {

    return <Text
        key={shapeProps.id}
        shapeProps={shapeProps as TextShape}
        onSelect={onSelect}
    />
};





function transformToInternalText(s: JSONGeometricShape): GeometricShape {
    const shape = s as TextShape
    return rotateAnchoredShapeToTransform(shape);
}

function globalText(s: GeometricShape) {
    const shape = s as TextShape;
    const transformed = {
        ...s,
        ...getAnchor(shape)
    }
    const { transformation, ...remainingShape } = transformed;
    return remainingShape;
}

function textIntersectCheck(s: GeometricShape, r: RectSel): boolean {
    const shape = s as TextShape;
    const trans = new Transform(shape.transformation);
    const pointsAbsolutePosition: Point2d = trans.point2d(shape)
    const segments = [{ start: pointsAbsolutePosition, end: trans.point2d({ x: pointsAbsolutePosition.x + (shape.width ?? 0), y: pointsAbsolutePosition.y + (shape.height ?? 0) }) }]
    return lineRectIntersection(segments, r)
}

export { createText, createTextBase, createTextRubber, textIntersectCheck, globalText, transformToInternalText }
export default Text;